import { useEffect, useRef, useState } from 'react'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import type { BarcodeScanOutcome } from '@core/pages/PosPage'
import { validateScannedBarcode } from '@core/utils/barcodeValidator'
import { cx, hint } from '../../ui/classes'
import { Alert, Check, Close, Pencil, Torch } from '../../ui/icons'

const ITEM_WIDGET =
  'absolute inset-x-3 z-15 rounded-panel border-2 border-ok bg-white p-3 shadow-modal'
const OK_BANNER =
  'absolute inset-x-3 z-30 flex items-center justify-center gap-2.5 rounded-card bg-ok/90 px-3 py-2 text-center text-sm font-semibold text-white'
const HOLD_BANNER =
  'absolute inset-x-3 bottom-[84px] z-15 rounded-card bg-black/75 px-3 py-2 text-center text-small text-white/95'
const SUB_BTN =
  'min-w-11 shrink-0 cursor-pointer rounded-[10px] border border-lineStrong bg-soft px-3 py-2 text-base2 font-bold text-inkSoft'
const CTRL_BTN =
  'inline-flex h-10 w-10 items-center justify-center rounded-pill border border-lineStrong bg-soft text-ink'

interface CameraScannerModalProps {
  isOpen: boolean
  onClose: () => void
  /** Обработчик кода. Возвращает исход (найден / можно создать). */
  onScan: (text: string) => Promise<BarcodeScanOutcome>
  /** Изменить количество товара в чеке на delta. Возвращает новое количество. */
  onChangeItemQty?: (barcode: string, delta: number) => number
  /** Создать товар «на лету» прямо из видоискателя. */
  onCreateProduct?: (data: {
    name: string
    price: number
    barcode?: string
    isWeight?: boolean
    requiresMarking?: boolean
  }) => Promise<void>
  /** Модуль «Честный Знак» включён — показываем галочку маркировки. */
  czEnabled?: boolean
  /**
   * Заголовок-подсказка над видоискателем (напр. «Отсканируйте код маркировки»).
   * Задаётся вызывающей стороной под конкретную задачу съёмки: маркировка ЧЗ
   * требует DataMatrix, обычная продажа — линейный штрихкод.
   */
  prompt?: string
  /** Уточнение под заголовком (напр. название позиции, для которой нужен код). */
  subtitle?: string
}

/** Поддерживаемые форматы: линейные штрихкоды + DataMatrix/QR. */
const SUPPORTED_FORMATS: Html5QrcodeSupportedFormats[] = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.QR_CODE,
]

const SCANNER_ELEMENT_ID = 'camera-scanner-viewport'

const SCAN_CONFIG = {
  fps: 20,
  // Свою рамку рисует оверлей ниже. qrbox html5-qrcode на iOS не совпадает
  // с ней, поэтому вторую рамку не включаем.
  videoConstraints: {
    facingMode: 'environment',
    // min-значения ломали запуск на телефонах с камерой ниже 1280x720.
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
}

/** Проверить окружение до вызова getUserMedia и вернуть понятную причину. */
function cameraEnvironmentError(): string | null {
  if (typeof window === 'undefined') return 'Камера доступна только в браузере'
  if (!window.isSecureContext) {
    return (
      'Камера доступна только по HTTPS. ' +
      'Откройте PWA по защищённому адресу (https://), а не по IP/HTTP.'
    )
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return 'Этот браузер не поддерживает доступ к камере. Используйте Safari/Chrome последней версии.'
  }
  return null
}

/** Детализация ошибки getUserMedia для кассира и диагностики. */
function cameraErrorMessage(error: unknown): string {
  const name = error instanceof DOMException ? error.name : ''
  const text = error instanceof Error ? error.message : String(error)
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'Доступ к камере запрещён. Разрешите камеру для этого сайта в настройках браузера.'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'Камера не найдена на устройстве.'
    case 'NotReadableError':
    case 'TrackStartError':
      return 'Камера занята другим приложением. Закройте его и повторите.'
    case 'OverconstrainedError':
      return 'Камера не поддерживает запрошенное разрешение. Попробуйте другую камеру.'
    case 'SecurityError':
      return 'Браузер заблокировал камеру. Откройте приложение по HTTPS.'
    default:
      return `Не удалось запустить камеру: ${name ? `${name}: ` : ''}${text}`
  }
}

/** Окно «уберите товар» / «нет в каталоге». */
interface UnknownCodeState {
  barcode: string
  canCreate: boolean
  /** Определённый формат кода (для бейджа в карточке). */
  format?: string
}

/** Человеко-читаемый бейдж формата кода. */
function formatBadgeLabel(format?: string): string {
  switch (format) {
    case 'EAN-13':
      return 'EAN-13'
    case 'EAN-8':
      return 'EAN-8'
    case 'DATAMATRIX':
      return 'DataMatrix'
    case 'CODE-128':
      return 'Code-128'
    default:
      return 'Штрихкод'
  }
}

/**
 * Умный видоискатель сканера камерой.
 *
 * - Автовыбор объектива: start({facingMode:'environment'}); при ошибке — тихий
 *   фолбэк на getCameras() → первая тыловая камера. Без ручной кнопки смены.
 * - Дедупликация в режиме «Очередь»: один и тот же код считывается 1 раз.
 *   Разблокировка — при другом коде или после 1.5с без распознавания (кадры
 *   без кода отслеживаются через onScanFailure).
 * - Неизвестный товар: камера НЕ закрывается, видео на паузе, предупреждающий
 *   двойной бип 330Гц. Карточка поверх видео: при праве — инлайн-форма
 *   создания, без права — плашка с авто-скрытием.
 */
export function CameraScannerModal({
  isOpen,
  onClose,
  onScan,
  onChangeItemQty,
  onCreateProduct,
  czEnabled = false,
  prompt,
  subtitle,
}: CameraScannerModalProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)

  // Дедупликация (в ref, чтобы не пересоздавать коллбэки сканера).
  const lockedBarcodeRef = useRef<string | null>(null)
  const lastCodeSeenRef = useRef<number>(0)
  const busyRef = useRef(false) // идёт обработка/пауза — не принимаем новые коды
  // Метка последнего взаимодействия (нажатие кнопки / код в кадре).
  const lastInteractionTimeRef = useRef<number>(Date.now())
  // Двухкадровый консенсус: кандидат, ожидающий подтверждения вторым кадром.
  const candidateRef = useRef<{ code: string; time: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [torchOn, setTorchOn] = useState(false)
  const [zoom, setZoom] = useState(2)
  const [zoomSupported, setZoomSupported] = useState(false)
  const [torchSupported, setTorchSupported] = useState(false)

  const [status, setStatus] = useState<{ kind: 'ok' | 'hold'; text: string } | null>(null)
  // Активный отсканированный товар — виджет со счётчиком и кнопками [−/+].
  const [activeItem, setActiveItem] = useState<{
    barcode: string
    name: string
    price: number
    qty: number
  } | null>(null)
  const [unknownCode, setUnknownCode] = useState<UnknownCodeState | null>(null)
  const [inlineForm, setInlineForm] = useState<{
    name: string
    price: string
    barcode: string
    isWeight: boolean
    requiresMarking: boolean
  } | null>(null)
  const [saving, setSaving] = useState(false)
  const autoHideRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /** Приятный бип (успех) — 880Гц. */
  const beepOk = () => playTone(880, 0.07)
  /** Предупреждающий двойной низкий бип — 330Гц × 2. */
  const beepWarn = () => {
    playTone(330, 0.08)
    setTimeout(() => playTone(330, 0.08), 120)
  }
  const playTone = (freq: number, dur: number) => {
    try {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!AudioCtx) return
      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx()
      const ctx = audioCtxRef.current
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur)
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start()
      osc.stop(ctx.currentTime + dur + 0.01)
    } catch {
      /* звук недоступен */
    }
  }

  const stopScanner = async () => {
    const scanner = scannerRef.current
    if (!scanner) return
    try {
      if (scanner.isScanning) await scanner.stop()
      scanner.clear()
    } catch {
      /* уже остановлен */
    }
  }

  /** Активный видео-трек из DOM-элемента <video>. */
  const getActiveVideoTrack = (): MediaStreamTrack | null => {
    const videoEl = document.querySelector(
      `#${SCANNER_ELEMENT_ID} video`
    ) as HTMLVideoElement | null
    const stream = videoEl?.srcObject as MediaStream | null
    return stream?.getVideoTracks()[0] ?? null
  }

  /** Поддерживает ли текущий трек аппаратную вспышку. */
  const currentTrackHasTorch = (): boolean => {
    try {
      const track = getActiveVideoTrack()
      if (!track || typeof track.getCapabilities !== 'function') return false
      return 'torch' in (track.getCapabilities() || {})
    } catch {
      return false
    }
  }

  /** Пост-старт: автофокус и зум 2x по умолчанию. */
  const afterStart = async (scanner: Html5Qrcode) => {
    try {
      await scanner.applyVideoConstraints({
        advanced: [{ focusMode: 'continuous' }],
      } as unknown as MediaTrackConstraints)
    } catch {
      /* focusMode не поддержан */
    }

    // Даём треку инициализироваться, затем сразу пытаемся включить зум 2x.
    await new Promise((r) => setTimeout(r, 300))
    try {
      await scanner.applyVideoConstraints({
        // @ts-expect-error non-standard constraint
        advanced: [{ zoom: 2 }],
      })
      setZoom(2)
      setZoomSupported(true)
    } catch {
      setZoom(1)
      setZoomSupported(false)
    }

    // Фонарик: не перезапускаем камеру и не блокируем кнопку по
    // getCapabilities(). На Android Chrome torch часто доступен, даже если
    // capability не объявлена; окончательно это выясняется при нажатии.
    setTorchOn(false)
    setTorchSupported(currentTrackHasTorch())
  }

  // Жизненный цикл камеры.
  useEffect(() => {
    if (!isOpen) return
    const envError = cameraEnvironmentError()
    if (envError) {
      setError(envError)
      return
    }
    let cancelled = false
    setError(null)
    setStatus(null)
    setActiveItem(null)
    setUnknownCode(null)
    setInlineForm(null)
    setTorchSupported(false)
    lockedBarcodeRef.current = null
    candidateRef.current = null
    busyRef.current = false

    const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
      formatsToSupport: SUPPORTED_FORMATS,
      verbose: false,
    })
    scannerRef.current = scanner

    /** Успешное распознавание кадра. */
    const onScanSuccess = (decodedText: string) => {
      // Любой распознанный кадр — взаимодействие (продлевает жизнь виджета).
      lastCodeSeenRef.current = Date.now()
      lastInteractionTimeRef.current = Date.now()

      // 1. Математическая валидация: контрольная сумма GS1 + формат.
      //    Битые кадры (обрезки EAN-13, неверная контр. цифра) — тихий отброс
      //    без звуков и спама.
      const validation = validateScannedBarcode(decodedText)
      if (!validation.isValid) return
      const cleanCode = validation.sanitizedCode
      const now = Date.now()

      // 2. Двухкадровый консенсус: требуем два совпадающих кадра подряд в
      //    окне 400мс. Первый кадр лишь запоминаем как кандидата.
      if (
        !candidateRef.current ||
        candidateRef.current.code !== cleanCode ||
        now - candidateRef.current.time > 400
      ) {
        candidateRef.current = { code: cleanCode, time: now }
        return
      }
      // Консенсус достигнут — сбрасываем кандидата.
      candidateRef.current = null

      // 3. Жёсткая дедупликация: тот же заблокированный код — полный игнор.
      if (lockedBarcodeRef.current === cleanCode) return
      // Идёт обработка/пауза (неизвестный товар) — не принимаем новые коды.
      if (busyRef.current) return

      // Новый подтверждённый код — блокируем и обрабатываем.
      lockedBarcodeRef.current = cleanCode
      busyRef.current = true

      void onScan(cleanCode)
        .then((outcome) => {
          if (cancelled) return
          if (outcome.found) {
            beepOk()
            navigator.vibrate?.([80])
            lastInteractionTimeRef.current = Date.now()
            if (outcome.price == null && outcome.cartQty == null) {
              // Служебный скан (код маркировки / тары / «на лету»): виджет
              // количества неуместен — показываем подтверждение в баннере.
              setActiveItem(null)
              setStatus({ kind: 'ok', text: outcome.name ?? 'Код принят' })
            } else {
              // Виджет активного товара: имя, цена, актуальное кол-во в чеке.
              setActiveItem({
                barcode: cleanCode,
                name: outcome.name ?? 'Товар',
                price: outcome.price ?? 0,
                qty: outcome.cartQty ?? 1,
              })
              setStatus(null)
            }
            // Поточный режим: обработка завершена, блок держится до пропажи
            // кода из кадра / 5с тишины.
            busyRef.current = false
          } else {
            // Неизвестный товар: пауза видео, предупреждение, карточка.
            beepWarn()
            try {
              scanner.pause(true)
            } catch {
              /* пауза недоступна */
            }
            setUnknownCode({
              barcode: outcome.barcode,
              canCreate: Boolean(outcome.canCreate),
              format: validation.format,
            })
            if (!outcome.canCreate) {
              // Авто-скрытие плашки через 2.5с с возобновлением сканирования.
              if (autoHideRef.current) clearTimeout(autoHideRef.current)
              autoHideRef.current = setTimeout(() => dismissUnknown(), 2500)
            }
          }
        })
        .catch(() => {
          busyRef.current = false
        })
    }

    /** Кадр без штрихкода — сброс дедуп-блокировки/виджета.
     *  Виджет и блокировка снимаются только после 5с ПОЛНОЙ неактивности:
     *  кассир ничего не нажимает и штрихкода нет в кадре. Любое нажатие
     *  кнопок или появление кода в кадре продлевает жизнь виджета. */
    const onScanFailure = () => {
      if (
        lockedBarcodeRef.current &&
        !busyRef.current &&
        Date.now() - lastInteractionTimeRef.current > 5000
      ) {
        lockedBarcodeRef.current = null
        setStatus(null)
        setActiveItem(null)
      }
    }

    void (async () => {
      try {
        await scanner.start({ facingMode: 'environment' }, SCAN_CONFIG, onScanSuccess, onScanFailure)
      } catch (err) {
        // Тихий фолбэк: перечисление камер → первая тыловая.
        try {
          const devices = await Html5Qrcode.getCameras()
          const backCam = devices.find((d) => !/front|selfie|user/i.test(d.label)) || devices[0]
          if (backCam) {
            await scanner.start(backCam.id, SCAN_CONFIG, onScanSuccess, onScanFailure)
          } else {
            throw err
          }
        } catch (err2) {
          setError(cameraErrorMessage(err2))
          return
        }
      }
      if (!cancelled) await afterStart(scanner)
    })()

    return () => {
      cancelled = true
      if (autoHideRef.current) clearTimeout(autoHideRef.current)
      void stopScanner()
      scannerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  /** Сбросить окно «нет в каталоге» и возобновить сканирование. */
  const dismissUnknown = () => {
    if (autoHideRef.current) clearTimeout(autoHideRef.current)
    setUnknownCode(null)
    setInlineForm(null)
    busyRef.current = false
    lockedBarcodeRef.current = null
    const scanner = scannerRef.current
    try {
      scanner?.resume()
    } catch {
      /* resume недоступен */
    }
  }

  /** Сохранить товар из инлайн-формы и возобновить сканирование. */
  const submitInline = async () => {
    if (!inlineForm || !unknownCode || !onCreateProduct) return
    const price = Number(inlineForm.price.replace(',', '.'))
    const barcode = inlineForm.barcode.trim()
    if (!inlineForm.name.trim()) {
      setStatus({ kind: 'hold', text: 'Укажите наименование' })
      return
    }
    if (!Number.isFinite(price) || price < 0) {
      setStatus({ kind: 'hold', text: 'Укажите корректную цену' })
      return
    }
    if (!barcode) {
      setStatus({ kind: 'hold', text: 'Укажите штрихкод' })
      return
    }
    setSaving(true)
    try {
      await onCreateProduct({
        name: inlineForm.name,
        price,
        barcode,
        isWeight: inlineForm.isWeight,
        requiresMarking: inlineForm.requiresMarking
      })
      beepOk()
      setStatus({ kind: 'ok', text: 'Товар добавлен' })
      dismissUnknown()
    } catch (err) {
      setStatus({ kind: 'hold', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setSaving(false)
    }
  }

  /** Изменить количество активного товара в чеке (+1 / −1) без повторного скана. */
  const changeActiveQty = (delta: number) => {
    const item = activeItem
    if (!item) return

    // Любое нажатие — взаимодействие: продлеваем жизнь виджета (keep-alive).
    lastInteractionTimeRef.current = Date.now()

    if (delta > 0) {
      // Добавление: через основной обработчик (поддерживает «на лету» и т.п.).
      void onScan(item.barcode)
        .then((outcome) => {
          if (!outcome.found) return
          beepOk()
          navigator.vibrate?.([60])
          setActiveItem((prev) =>
            prev ? { ...prev, qty: outcome.cartQty ?? prev.qty + 1 } : prev
          )
        })
        .catch(() => {
          /* ошибка — игнор, товар уже в чеке */
        })
      return
    }

    // Уменьшение: напрямую через onChangeItemQty.
    if (!onChangeItemQty) return
    const newQty = onChangeItemQty(item.barcode, delta)
    beepOk()
    navigator.vibrate?.([40])
    if (newQty <= 0) {
      setActiveItem(null)
    } else {
      setActiveItem((prev) => (prev ? { ...prev, qty: newQty } : prev))
    }
  }

  /**
   * Переключение фонарика напрямую через нативный видео-трек.
   *
   * Констрейнт — строго чистый `{ torch: next }` БЕЗ примеси zoom: на части
   * Android Chrome смешанный `{ torch, zoom }` бракуется целиком. Берём трек
   * из DOM-элемента <video>; фолбэк — штатный метод html5-qrcode.
   */
  const toggleTorch = async () => {
    const next = !torchOn
    try {
      // 1. Нативный трек напрямую из <video>.
      const track = getActiveVideoTrack()
      if (track) {
        await track.applyConstraints({
          // @ts-expect-error non-standard constraint
          advanced: [{ torch: next }],
        })
      } else {
        // 2. Фолбэк через метод html5-qrcode.
        const scanner = scannerRef.current
        if (!scanner) throw new Error('Камера не запущена')
        await scanner.applyVideoConstraints({
          advanced: [{ torch: next }],
        } as unknown as MediaTrackConstraints)
      }
      setTorchOn(next)
      setTorchSupported(true)
    } catch (err) {
      console.warn('[Camera] Torch toggle failed:', err)
      setTorchOn(false)
      setStatus({ kind: 'hold', text: 'Фонарик недоступен на этой камере' })
      setTimeout(() => setStatus(null), 3000)
    }
  }

  const toggleZoom = async () => {
    const scanner = scannerRef.current
    if (!scanner) return
    const nextZoom = zoom === 1 ? 2 : 1
    try {
      await scanner.applyVideoConstraints({ advanced: [{ zoom: nextZoom }] } as unknown as MediaTrackConstraints)
      setZoom(nextZoom)
    } catch {
      /* zoom не поддержан */
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-camera flex flex-col items-center justify-center bg-black" role="dialog" aria-modal="true" aria-label="Сканер камерой">
      <button className="absolute right-4 top-4 z-40 flex h-10 w-10 items-center justify-center rounded-pill border-0 bg-black/60 text-xl text-white" onClick={() => void stopScanner().then(onClose)} aria-label="Закрыть">
        <Close size={20} />
      </button>

      {error ? (
        <div className="rounded-panel border border-warnLine bg-red-50 px-4 py-3 text-sm text-dangerDark">{error}</div>
      ) : (
        <>
          
          <div className="relative aspect-[3/4] w-full max-w-[480px] overflow-hidden bg-black">
            <div id={SCANNER_ELEMENT_ID} className="h-full w-full" />

            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[140px] w-[280px] max-w-[85%] -translate-x-1/2 -translate-y-1/2 rounded-panel shadow-[0_0_0_9999px_rgba(0,0,0,0.65)]" aria-hidden="true">
              <span className="absolute left-[-1px] top-[-1px] h-4 w-4 rounded-tl-card border-l-[3px] border-t-[3px] border-white" />
              <span className="absolute right-[-1px] top-[-1px] h-4 w-4 rounded-tr-card border-r-[3px] border-t-[3px] border-white" />
              <span className="absolute bottom-[-1px] left-[-1px] h-4 w-4 rounded-bl-card border-b-[3px] border-l-[3px] border-white" />
              <span className="absolute bottom-[-1px] right-[-1px] h-4 w-4 rounded-br-card border-b-[3px] border-r-[3px] border-white" />
              <div className="absolute inset-x-2 top-1/2 h-0.5 -translate-y-1/2 bg-red-500/80 shadow-[0_0_6px_rgba(239,68,68,0.55)]" />
              <div className="absolute -top-6 w-full text-center text-tiny font-semibold text-white/95">
                {prompt ? 'Оптимально: DataMatrix в рамке' : 'Оптимально: 15–20 см'}
              </div>
            </div>

            {/* Подсказка под задачу съёмки: маркировка ЧЗ → DataMatrix. */}
            {prompt && (
              <div className="pointer-events-none absolute inset-x-3 top-3 z-30 flex flex-col gap-0.5 rounded-panel border border-slate-400/45 bg-ink/80 px-3.5 py-2.5 text-center text-sm text-slate-50">
                <strong>{prompt}</strong>
                {subtitle && <span className="truncate text-tiny text-slate-300">{subtitle}</span>}
              </div>
            )}

            {/* Интерактивный виджет товара со счётчиком (оба режима). */}
            {activeItem && !unknownCode ? (
              <div className={cx(ITEM_WIDGET, prompt ? 'top-[84px]' : 'top-3')}>
                <div className="mb-2 flex items-center gap-1.5 truncate text-base2 font-bold text-ink" title={activeItem.name}>
                  <Check size={16} className="shrink-0 text-okDark" /> {activeItem.name}
                </div>
                <div className="flex items-center justify-between gap-2.5">
                  <div className="whitespace-nowrap text-base2 font-bold text-okDark">
                    В чеке: {activeItem.qty} шт
                    <span className="text-ink">
                      {' · '}
                      {Number((activeItem.qty * activeItem.price).toFixed(2))} ₽
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      className={cx(SUB_BTN, activeItem.qty <= 1 && 'cursor-not-allowed opacity-40 shadow-none')}
                      disabled={activeItem.qty <= 1}
                      onClick={() => changeActiveQty(-1)}
                      aria-label="Уменьшить количество"
                    >
                      −
                    </button>
                    <button
                      className="shrink-0 cursor-pointer rounded-[10px] border-2 border-okDark bg-ok px-4 py-2 text-sm font-bold text-white"
                      onClick={() => changeActiveQty(1)}
                      aria-label="Добавить одну штуку"
                    >
                      + 1 шт
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              status && (
                <div
                  className={
                    status.kind === 'ok'
                      ? cx(OK_BANNER, prompt ? 'top-[84px]' : 'top-3')
                      : HOLD_BANNER
                  }
                >
                  {status.text}
                </div>
              )
            )}
            {/* Подсказка над панелью контролов, пока код зафиксирован. */}
            {!status && !activeItem && lockedBarcodeRef.current && !unknownCode && (
              <div className="absolute inset-x-3 bottom-[84px] z-15 rounded-card bg-black/75 px-3 py-2 text-center text-small text-white/95">Штрихкод зафиксирован. Поднесите следующий товар</div>
            )}

            {/* Карточка неизвестного товара поверх видео. */}
            {unknownCode && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 p-4">
                {unknownCode.canCreate ? (
                  <div className="w-full max-w-[320px] rounded-panel bg-white p-4 text-center shadow-modal">
                    {!inlineForm ? (
                      <>
                        <div className="mb-2 flex flex-wrap items-center justify-center gap-1.5">
                          <span className="rounded-pill bg-sky-100 px-2.5 py-1 text-micro font-bold text-info">{formatBadgeLabel(unknownCode.format)}</span>
                        </div>
                        <p className="mb-2 flex items-center justify-center gap-1.5 text-base2 font-bold text-ink">
                          <Alert size={18} className="shrink-0 text-amber-600" /> Товара со штрихкодом {unknownCode.barcode} нет в каталоге
                        </p>
                        <div className="flex gap-2">
                          <button
                            className="h-10 flex-1 rounded-card border border-ok bg-ok px-3 text-sm font-bold text-white"
                            onClick={() =>
                              setInlineForm({
                                name: '',
                                price: '',
                                barcode: unknownCode.barcode,
                                isWeight: false,
                                requiresMarking: false
                              })
                            }
                          >
                            + Добавить сейчас
                          </button>
                          <button className="h-10 flex-1 rounded-card border border-lineStrong bg-white px-3 text-sm font-semibold text-ink" onClick={dismissUnknown}>
                            Пропустить
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mb-2 flex flex-wrap items-center justify-center gap-1.5">
                          <span className="rounded-pill bg-sky-100 px-2.5 py-1 text-micro font-bold text-info">{formatBadgeLabel(unknownCode.format)}</span>
                        </div>
                        <p className="mb-2 text-base2 font-bold text-ink">Новый товар</p>
                        <input
                          autoFocus
                          placeholder="Наименование"
                          value={inlineForm.name}
                          onChange={(e) => setInlineForm((f) => (f ? { ...f, name: e.target.value } : f))}
                          className="w-full rounded-card border border-lineStrong px-2.5 py-1.5 text-sm"
                        />
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          placeholder="Цена, ₽"
                          value={inlineForm.price}
                          onChange={(e) => setInlineForm((f) => (f ? { ...f, price: e.target.value } : f))}
                          className="w-full rounded-card border border-lineStrong px-2.5 py-1.5 text-sm"
                        />
                        {/* Редактируемый штрихкод: кассир может исправить
                            опечатку сканера перед добавлением. */}
                        <div className="flex items-center gap-2">
                          <Pencil size={18} className="text-muted" />
                          <input
                            inputMode="numeric"
                            placeholder="Штрихкод"
                            value={inlineForm.barcode}
                            onChange={(e) =>
                              setInlineForm((f) => (f ? { ...f, barcode: e.target.value } : f))
                            }
                            className="w-full flex-1 rounded-card border border-lineStrong px-2.5 py-1.5 text-sm"
                          />
                        </div>
                        <div className="mt-1 flex flex-wrap items-center justify-center gap-3 text-micro">
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={inlineForm.isWeight}
                              onChange={(e) =>
                                setInlineForm((f) =>
                                  f ? { ...f, isWeight: e.target.checked } : f
                                )
                              }
                              className="h-4 w-4 accent-ok"
                            />
                            весовой
                          </label>
                          {czEnabled && (
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={inlineForm.requiresMarking}
                                onChange={(e) =>
                                  setInlineForm((f) =>
                                    f ? { ...f, requiresMarking: e.target.checked } : f
                                  )
                                }
                                className="h-4 w-4 accent-ok"
                              />
                              маркировка
                            </label>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button className="h-10 flex-1 rounded-card border border-ok bg-ok px-3 text-sm font-bold text-white" disabled={saving} onClick={() => void submitInline()}>
                            {saving ? 'Сохранение…' : '+ Добавить сейчас'}
                          </button>
                          <button className="h-10 flex-1 rounded-card border border-lineStrong bg-white px-3 text-sm font-semibold text-ink" disabled={saving} onClick={dismissUnknown}>
                            Отмена
                          </button>
                        </div>
                        {status?.kind === 'hold' && <p className="mt-2 text-small text-danger">{status.text}</p>}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="w-full max-w-[320px] rounded-panel bg-white p-4 text-center shadow-modal">
                    <p className="mb-2 flex items-center justify-center gap-1.5 text-base2 font-bold text-ink">
                      <Alert size={18} className="shrink-0 text-danger" /> Товара со штрихкодом {unknownCode.barcode} нет в каталоге
                    </p>
                    <p className="mb-3 text-small text-muted">
                      Для добавления товара обратитесь к администратору
                    </p>
                    <button className="h-10 flex-1 rounded-card border border-lineStrong bg-white px-3 text-sm font-semibold text-ink" onClick={dismissUnknown}>
                      Понятно
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Плавающая нижняя панель контролов поверх видеопотока. */}
            <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3 rounded-pill bg-ink/85 px-4 py-2 backdrop-blur">
              {zoomSupported && (
                <button className="h-10 w-10 rounded-pill border border-lineStrong bg-soft text-ink" onClick={() => void toggleZoom()}>
                  {zoom === 1 ? '1x' : '2x'}
                </button>
              )}
              <button
                className={cx(
                  CTRL_BTN,
                  torchOn ? 'bg-amber-400/90 text-black' : 'bg-soft text-ink'
                )}
                onClick={() => void toggleTorch()}
                aria-label="Фонарик"
                title={torchSupported ? 'Фонарик' : 'Попробовать включить фонарик'}
              >
                <Torch size={20} />
              </button>
              <button className="inline-flex h-10 items-center gap-1.5 rounded-pill bg-ok px-4 text-sm font-bold text-white" onClick={() => void stopScanner().then(onClose)}>
                <Close size={18} /> Готово
              </button>
            </div>
          </div>

          <p className={hint}>
            {prompt
              ? 'Наведите камеру на код DataMatrix на упаковке'
              : 'Держите штрихкод на расстоянии 15–20 см'}
          </p>
        </>
      )}
    </div>
  )
}

